import { useContext, useEffect, useMemo, useState } from "react";
import * as tagMetadataActions from "../../actions/tagMetadataActions";
import { Context } from "../../components/context";
import { AppContext } from "../../models/applicationState";
import { withApplicationInsights } from "../../components/telemetry";
import { useNavigate } from "react-router-dom";
import { Box, CssBaseline, TextField, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { GridActionsCellItem, GridRowId } from "@mui/x-data-grid";
import EditableGrid from "../../components/editableGrid";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { TooltipIcon } from "../../components/tooltipIcon";
import CustomToolbar from "../../components/customToolbar";
import {
  ALL_TAGS,
  TagMetadata,
  UNCATEGORIZED,
  decisionsColumn,
  projectNameColumn,
  tagNameColumn,
  tasksColumn,
} from "../../models/tagMetadata";
import { useTranslation } from "react-i18next";
import {
  LIBRARY_PAGE_HEADER_HEIGHT,
  LibraryPageHeader,
} from "../../components/libraryPageHeader";

const TagLibraryPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const appContext = useContext<AppContext>(Context);
  const selectedProjectFilters = appContext.state.selectedProjectFilters;
  const [tagMetadata, setTagMetadata] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const projects = useMemo(
    () => appContext.state.invoker?.projects || [],
    [appContext.state.invoker?.projects],
  );

  // Debounce search input - wait 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Derived: filtered grid items. useMemo (synchronous) avoids the empty→filtered double paint.
  const gridItems = useMemo(() => {
    if (!debouncedSearchText) return tagMetadata;
    const lower = debouncedSearchText.toLowerCase();
    return tagMetadata.filter(
      (item: any) =>
        item.tagName?.toLowerCase().includes(lower) ||
        item.projectName?.toLowerCase().includes(lower),
    );
  }, [tagMetadata, debouncedSearchText]);

  const tagColumns = useMemo(
    () => [projectNameColumn, tagNameColumn, decisionsColumn, tasksColumn],
    [],
  );

  function convertTagMetadataToGridData(tagMetadata: TagMetadata) {
    const project = projects?.find(
      (project) => project.id === tagMetadata.projectId,
    );
    const tag = project?.tags.find((tag) => tag.id === tagMetadata.tagId);
    const tagName =
      tagMetadata.tagId === UNCATEGORIZED
        ? t("tags.uncategorized")
        : tagMetadata.tagId === ALL_TAGS
          ? t("tags.all")
          : tag?.name || t("tags.not_shared");
    return {
      id: tagMetadata.id,
      tagName: tagName,
      tagId: tagMetadata.tagId,
      projectName:
        tagMetadata.projectId === UNCATEGORIZED
          ? t("tags.uncategorized")
          : project?.name || t("tags.not_shared"),
      projectId: tagMetadata.projectId,
      decisions: tagMetadata.decisions,
      infos: tagMetadata.infos,
      tasks: tagMetadata.tasks,
    };
  }
  async function fetchData() {
    const response = await tagMetadataActions.list();
    const mapped = response.map(convertTagMetadataToGridData);
    // Sort by project name; equal project names are sorted by tag name
    setTagMetadata(
      mapped.sort((a, b) => {
        if (a.projectName === b.projectName) {
          return a.tagName.localeCompare(b.tagName);
        }
        return a.projectName.localeCompare(b.projectName);
      }),
    );
  }

  useEffect(() => {
    fetchData();
  }, [selectedProjectFilters, navigate]);

  const onNavigate = (tag: TagMetadata) => {
    if (tag) {
      navigate(`/tags/${tag.projectId}/${tag.tagId}`);
    }
  };

  const onMeetingUpserted = async (
    _newEntry: TagMetadata,
    _oldEntry?: TagMetadata,
  ) => {
    return;
  };

  const gridActions = (id: GridRowId, row: TagMetadata) => {
    const handleNavigate = (_id: GridRowId) => () => {
      onNavigate(row);
    };

    return [
      <GridActionsCellItem
        key="open"
        icon={
          <TooltipIcon titleKey={"general.open"} icon={<ChevronRightIcon />} />
        }
        label="Open"
        onClick={handleNavigate(id)}
        style={{ color: "var(--mui-palette-primary-main)" }}
      />,
    ];
  };

  return (
    <Box
      sx={{
        pt: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "& .scroll-overflow": {
          overflow: "auto !important",
        },
      }}
    >
      <CssBaseline />
      <LibraryPageHeader
        title={t("menu.my_tags")}
        chipLabel={t("tags.workstream", { count: gridItems.length })}
        rightContent={
          <TextField
            size="small"
            placeholder={t("library.searchPlaceholder")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              width: 250,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
              },
            }} slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ color: "text.secondary", fontSize: "1.25rem" }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchText("")}
                    edge="end"
                    sx={{ padding: "4px" }}
                  >
                    <ClearIcon sx={{ fontSize: "1.25rem" }} />
                  </IconButton>
                </InputAdornment>
              ),
            } }}
          />
        }
      />
      <Box sx={{ flexGrow: 1 }}>
        <EditableGrid
          processRowUpdate={onMeetingUpserted}
          rows={gridItems}
          actionsHeaderKey=""
          columns={tagColumns}
          height={`calc(90vh - ${LIBRARY_PAGE_HEADER_HEIGHT}px)`}
          extraActions={gridActions}
          modelType={""}
          toolbar={() => null}
        />
      </Box>
    </Box>
  );
};

export default withApplicationInsights(TagLibraryPage, "TagLibraryPage");
